import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Play } from "lucide-react";

const SCREEN_IMAGES = [
  "https://customer-assets.emergentagent.com/job_rep/artifacts/17wdgvuc_WhatsApp%20Image%202026-04-07%20at%204.31.47%20PM.jpeg",
  "https://customer-assets.emergentagent.com/job_rep/artifacts/5t9df0mg_WhatsApp%20Image%202026-04-07%20at%204.31.47%20PM%20%282%29.jpeg",
  "https://customer-assets.emergentagent.com/job_rep/artifacts/mlgp487k_WhatsApp%20Image%202026-04-07%20at%204.31.46%20PM%20%281%29.jpeg",
  "https://customer-assets.emergentagent.com/job_rep/artifacts/bqkbbjix_WhatsApp%20Image%202026-04-07%20at%204.31.47%20PM%20%281%29.jpeg",
  "https://customer-assets.emergentagent.com/job_rep/artifacts/f2gzsz75_WhatsApp%20Image%202026-04-07%20at%204.31.46%20PM.jpeg",
];

/* ── Phone Mockup Component ── */
const PhoneMockup = () => {
  const [activeScreen, setActiveScreen] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveScreen((prev) => (prev + 1) % SCREEN_IMAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      data-testid="hero-phone-mockup"
      className="relative mx-auto"
      style={{
        width: "260px",
        height: "520px",
        borderRadius: "40px",
        border: "6px solid #0D0D0D",
        overflow: "hidden",
        boxShadow: "0 25px 60px rgba(0,0,0,0.08)",
        backgroundColor: "#111",
      }}
    >
      {/* Notch */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-[#0D0D0D] rounded-b-2xl z-10" />
      <AnimatePresence mode="wait">
        <motion.img
          key={activeScreen}
          src={SCREEN_IMAGES[activeScreen]}
          alt="FitPulse app screen"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="absolute inset-0 w-full h-full object-contain"
        />
      </AnimatePresence>
    </div>
  );
};

/* ── Hero Section ── */
const HeroSection = () => {
  return (
    <section
      data-testid="hero-section"
      className="min-h-screen flex items-center pt-16 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16 py-12 lg:py-0">
          {/* Left content */}
          <div className="flex-1 lg:max-w-[60%] text-center lg:text-left">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-xs tracking-[0.2em] uppercase font-semibold text-[#999] mb-4"
            >
              Free Fitness Tracker for Android
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-4xl sm:text-5xl lg:text-[64px] font-bold text-[#0D0D0D] leading-[1.05] tracking-tight mb-6"
            >
              Your fitness.
              <br />
              Tracked. Perfected.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-base sm:text-lg lg:text-xl text-[#666] leading-relaxed mb-8 max-w-lg mx-auto lg:mx-0"
            >
              Log workouts, build habits, track progress, and celebrate every personal record. All in one beautifully simple app.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-6"
            >
              <motion.a
                data-testid="hero-download-button"
                href="https://play.google.com/store"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2.5 bg-[#0D0D0D] text-white font-semibold px-7 py-3.5 rounded-full text-sm"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <Play size={16} fill="white" />
                Download on Google Play
              </motion.a>
              <motion.button
                data-testid="hero-see-how-button"
                onClick={() => {
                  document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="inline-flex items-center justify-center gap-2 border border-[#D0D0D0] text-[#0D0D0D] font-semibold px-7 py-3.5 rounded-full text-sm hover:border-[#0D0D0D] transition-colors"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                See how it works
              </motion.button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="flex flex-wrap gap-4 sm:gap-6 justify-center lg:justify-start text-sm text-[#999]"
            >
              {["No ads", "Works offline"].map((text) => (
                <span key={text} className="flex items-center gap-1.5">
                  <Check size={14} className="text-[#0D0D0D]" />
                  {text}
                </span>
              ))}
            </motion.div>
          </div>

          {/* Right phone mockup */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="flex-shrink-0"
          >
            <PhoneMockup />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
